import { useMemo, useRef, useState } from 'react';

import { DropdownMenu2, dropdownMenu2Classes, Tooltip } from '@kc-console/common-ui';
import { useMegaMenuQuery, useScopedLinkBuilder, useServiceAppState } from '@kc-console/context';
import { useTranslation } from '@kc-console/core';
import IcCompanyRegular from '@kep/kcds-icons/IcCompanyRegular';

import GnbIconButton from './GnbIconButton';

import type { DropdownMenu2ItemData, DropdownMenu2OptionItemData } from '@kc-console/common-ui';

// Meta admin 에서 순서 변경 스펙 반영되면 하드코딩 제거 예정
const ORGANIZATION_SERVICE_KEYWORDS = ['billing', 'iam'] as const;

export default function OrganizationMenu() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement | null>(null);
  const { getProps } = useScopedLinkBuilder();
  const {
    state: { domainName },
  } = useServiceAppState();
  const { data: categories = [] } = useMegaMenuQuery();

  const organizationServices = useMemo(() => {
    const services = categories.flatMap(category => category.services);

    return ORGANIZATION_SERVICE_KEYWORDS.map(keyword =>
      services.find(service => service.keyword === keyword),
    ).filter(service => service !== undefined);
  }, [categories]);

  const items = useMemo<DropdownMenu2ItemData[]>(() => {
    const sectionItem: DropdownMenu2ItemData = {
      type: 'section',
      title: t('layout:gnb.organization.management'),
      header: domainName,
      expandable: false,
    };

    const serviceItems: DropdownMenu2ItemData[] = organizationServices.map((service, index) => {
      const menuItems: DropdownMenu2OptionItemData[] = service.menus.map(menu => {
        const { onClick } = getProps(menu.path, { isAnchor: false });
        return {
          text: menu.name,
          showDividerAbove: false,
          onClick: event => onClick(event),
        };
      });

      return {
        type: 'section',
        expandable: true,
        title: service.name,
        items: menuItems,
      };
    });

    return [sectionItem, ...serviceItems];
  }, [getProps, organizationServices, t, domainName]);

  return (
    <>
      <Tooltip message={t('layout:gnb.organization.management')}>
        <div ref={anchor}>
          <GnbIconButton
            disableRipple
            aria-expanded={open}
            aria-haspopup="menu"
            aria-label={t('layout:gnb.organization.management')}
            onClick={() => setOpen(prev => !prev)}
          >
            <IcCompanyRegular />
          </GnbIconButton>
        </div>
      </Tooltip>
      <DropdownMenu2
        anchorEl={anchor.current}
        open={open}
        expandable
        items={items}
        origin="bottom left"
        marginThreshold={0}
        onClose={() => setOpen(false)}
        css={{
          [`.${dropdownMenu2Classes.paper}`]: {
            minWidth: 268,
          },
        }}
      />
    </>
  );
}
